import React from 'react';
import type { Portfolio, Fund } from 'venn-api';
import type { SearchMenuItem } from '../../search-menu';
import { AllocationSearchProvider, useAllocationSearch } from './AllocationSearchContext';
import { AllocationSearchConstituentViewRedesigned } from './AllocationSearchConstituentView.redesign';
import { AllocationSearchInvestmentViewRedesigned } from './AllocationSearchInvestmentView.redesign';
import { AllocationSearchStrategyViewRedesigned } from './AllocationSearchStrategyView.redesign';
import { AllocationSearchAddTaxInformationViewRedesigned } from './AllocationSearchAddTaxInformationView.redesign';
import { useConstituentPortfolios } from './hooks/useConstituentPortfolios';
import { isEmpty } from 'lodash';
import { AddInvestmentCommonModal, TAX_MODAL_WIDTH } from '../../modals/add-investment-common-modal';
import type { LotInput } from './tax-info';

interface AllocationSearchContainerRedesignedProps {
  onClose: () => void;
  onSingleSelectFund: (item: SearchMenuItem, constituentId?: number) => void;
  onStrategyAssignment?: (fund: Fund, strategyId: string, constituentId?: number) => void;
  onAddTaxInformation: (selectedInvestmentId: string, lots: LotInput[]) => void;
  strategy: Portfolio;
  hasTaxInformation: boolean;
}

interface AllocationSearchContentRedesignedProps extends AllocationSearchContainerRedesignedProps {
  constituents: Portfolio[] | null;
}

const AllocationSearchContent: React.FC<AllocationSearchContentRedesignedProps> = ({
  onClose,
  onAddTaxInformation,
  strategy,
  constituents,
  hasTaxInformation,
}) => {
  const { view } = useAllocationSearch();

  if (view === 'STRATEGY_ASSIGNMENT') {
    return (
      <AddInvestmentCommonModal onClose={onClose}>
        <AllocationSearchStrategyViewRedesigned onClose={onClose} hasTaxInformation={hasTaxInformation} />
      </AddInvestmentCommonModal>
    );
  }

  if (view === 'CONSTITUENT_SELECTION' && constituents) {
    return (
      <AddInvestmentCommonModal onClose={onClose}>
        <AllocationSearchConstituentViewRedesigned onClose={onClose} constituents={constituents} />
      </AddInvestmentCommonModal>
    );
  }

  if (view === 'ADD_TAX_INFORMATION') {
    return (
      <AddInvestmentCommonModal onClose={onClose} size={TAX_MODAL_WIDTH}>
        <AllocationSearchAddTaxInformationViewRedesigned
          strategy={strategy}
          onAddTaxInformation={onAddTaxInformation}
          onClose={onClose}
        />
      </AddInvestmentCommonModal>
    );
  }

  return (
    <AddInvestmentCommonModal onClose={onClose}>
      <AllocationSearchInvestmentViewRedesigned onClose={onClose} strategy={strategy} allowBackStep={!!constituents} />
    </AddInvestmentCommonModal>
  );
};

export const AllocationSearchContainerRedesigned: React.FC<AllocationSearchContainerRedesignedProps> = (props) => {
  const { constituents, isLoading } = useConstituentPortfolios(props.strategy);

  if (isLoading) {
    return null;
  }

  const initialView = isEmpty(constituents) ? 'INVESTMENT_SEARCH' : 'CONSTITUENT_SELECTION';

  return (
    <AllocationSearchProvider
      initialView={initialView}
      onSingleSelectFund={props.onSingleSelectFund}
      onStrategyAssignment={props.onStrategyAssignment}
      hasTaxInformation={props.hasTaxInformation}
    >
      <AllocationSearchContent {...props} constituents={constituents} />
    </AllocationSearchProvider>
  );
};
